import React, { useEffect, useState } from "react";
import {
  Alert, Avatar, Badge, Button, Card, Col, Input, Modal, Popconfirm,
  Row, Select, Space, Spin, Table, Tag, Tooltip, Typography, Form,
} from "antd";
import {
  PlusOutlined, EditOutlined, LockOutlined, UserOutlined,
  CheckCircleOutlined, StopOutlined, KeyOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

export default function FrmUsuarioLista() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [alerta, setAlerta] = useState(null);
  const [busqueda, setBusqueda] = useState("");

  // Modal cambiar contraseña
  const [modalPwd, setModalPwd] = useState(false);
  const [usuarioPwd, setUsuarioPwd] = useState(null);
  const [formPwd] = Form.useForm();
  const [guardandoPwd, setGuardandoPwd] = useState(false);

  const cargar = async () => {
    try {
      setCargando(true);
      const [uRes, rRes] = await Promise.all([
        axios.get(`${API_URL}/Usuario`),
        axios.get(`${API_URL}/Rol`),
      ]);
      const uLista = Array.isArray(uRes.data?.$values) ? uRes.data.$values
        : Array.isArray(uRes.data) ? uRes.data : [];
      const rLista = Array.isArray(rRes.data?.$values) ? rRes.data.$values
        : Array.isArray(rRes.data) ? rRes.data : [];
      setUsuarios(uLista);
      setRoles(rLista);
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cargar usuarios." });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const cambiarEstado = async (id, estadoActual) => {
    try {
      const nuevoEstado = estadoActual === 1 ? 0 : 1;
      await axios.patch(`${API_URL}/Usuario/${id}/estado`, { estado: nuevoEstado });
      await cargar();
      setAlerta({ tipo: "success", texto: nuevoEstado === 1 ? "✅ Usuario activado." : "⚠️ Usuario desactivado." });
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cambiar estado." });
    }
  };

  const cambiarRol = async (id, rolId) => {
    try {
      await axios.patch(`${API_URL}/Usuario/${id}/rol`, { rolId });
      await cargar();
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cambiar rol." });
    }
  };

  const guardarPassword = async () => {
    try {
      const values = await formPwd.validateFields();
      setGuardandoPwd(true);
      await axios.patch(`${API_URL}/Usuario/${usuarioPwd.id}/password`, {
        nuevaPassword: values.nuevaPassword,
      });
      setModalPwd(false);
      formPwd.resetFields();
      setAlerta({ tipo: "success", texto: "✅ Contraseña actualizada correctamente." });
    } catch (err) {
      if (err?.errorFields) return;
      setAlerta({ tipo: "error", texto: "Error al cambiar contraseña." });
    } finally {
      setGuardandoPwd(false);
    }
  };

  const datos = usuarios.filter(u => {
    if (!busqueda) return true;
    const b = busqueda.toLowerCase();
    return (
      u.nombreUsuario?.toLowerCase().includes(b) ||
      u.nombre?.toLowerCase().includes(b) ||
      u.apellido?.toLowerCase().includes(b) ||
      u.email?.toLowerCase().includes(b)
    );
  });

  const activos   = usuarios.filter(u => u.estado === 1).length;
  const inactivos = usuarios.filter(u => u.estado !== 1).length;

  const columnas = [
    {
      title: "Usuario",
      key: "usuario",
      render: (_, r) => (
        <Space>
          <Avatar style={{ backgroundColor: r.estado === 1 ? "#1677ff" : "#bbb" }}
            icon={<UserOutlined />} size="small" />
          <div>
            <Text strong>{r.nombre} {r.apellido}</Text>
            <div><Text type="secondary" style={{ fontSize: 12 }}>@{r.nombreUsuario}</Text></div>
            {r.email && <div><Text type="secondary" style={{ fontSize: 11 }}>{r.email}</Text></div>}
          </div>
        </Space>
      ),
    },
    {
      title: "Rol / Permisos",
      key: "rol",
      width: 200,
      render: (_, r) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          value={r.rolId ?? null}
          placeholder="Sin rol asignado"
          allowClear
          onChange={val => cambiarRol(r.id, val ?? null)}
          options={roles.map(rol => ({
            value: rol.id,
            label: (
              <Space size={4}>
                {rol.esAdministrador && <Tag color="red" style={{ fontSize: 10, padding: "0 4px" }}>Admin</Tag>}
                {rol.nombre}
              </Space>
            ),
          }))}
        />
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      width: 100,
      align: "center",
      render: v => (
        <Badge
          status={v === 1 ? "success" : "default"}
          text={<Text style={{ fontSize: 13 }}>{v === 1 ? "Activo" : "Inactivo"}</Text>}
        />
      ),
    },
    {
      title: "Último acceso",
      dataIndex: "fechaUltimoAcceso",
      width: 140,
      render: v => v ? dayjs(v).format("DD/MM/YY HH:mm") : <Text type="secondary">Nunca</Text>,
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 130,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Editar datos">
            <Button size="small" icon={<EditOutlined />}
              onClick={() => navigate(`/dashboard/usuarios/${r.id}/editar`)} />
          </Tooltip>
          <Tooltip title="Cambiar contraseña">
            <Button size="small" icon={<KeyOutlined />}
              onClick={() => { setUsuarioPwd(r); formPwd.resetFields(); setModalPwd(true); }} />
          </Tooltip>
          <Tooltip title={r.estado === 1 ? "Desactivar usuario" : "Activar usuario"}>
            <Popconfirm
              title={r.estado === 1 ? "¿Desactivar este usuario?" : "¿Activar este usuario?"}
              onConfirm={() => cambiarEstado(r.id, r.estado)}
              okText="Sí" cancelText="No"
              okButtonProps={{ danger: r.estado === 1 }}>
              <Button
                size="small"
                icon={r.estado === 1 ? <StopOutlined /> : <CheckCircleOutlined />}
                danger={r.estado === 1}
                type={r.estado !== 1 ? "primary" : "default"}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>👤 Gestión de usuarios</Title>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => navigate("/dashboard/usuarios/nuevo")}>
          Nuevo usuario
        </Button>
      </div>

      {alerta && (
        <Alert type={alerta.tipo} message={alerta.texto} showIcon closable
          onClose={() => setAlerta(null)} style={{ marginBottom: 12 }} />
      )}

      {/* Tarjetas resumen */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={8} md={6}>
          <Card size="small">
            <Text type="secondary">Total</Text>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{usuarios.length}</div>
          </Card>
        </Col>
        <Col xs={8} md={6}>
          <Card size="small">
            <Text type="secondary">Activos</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#52c41a" }}>{activos}</div>
          </Card>
        </Col>
        <Col xs={8} md={6}>
          <Card size="small">
            <Text type="secondary">Inactivos</Text>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#bbb" }}>{inactivos}</div>
          </Card>
        </Col>
      </Row>

      {/* Buscador */}
      <Input.Search
        placeholder="Buscar por nombre, usuario o email..."
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        style={{ maxWidth: 400, marginBottom: 12 }}
        allowClear
      />

      <Spin spinning={cargando}>
        <Table
          rowKey="id"
          columns={columnas}
          dataSource={datos}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          size="middle"
          scroll={{ x: "max-content" }}
        />
      </Spin>

      {/* Modal cambiar contraseña */}
      <Modal
        title={<><KeyOutlined /> Cambiar contraseña — {usuarioPwd?.nombreUsuario}</>}
        open={modalPwd}
        onCancel={() => setModalPwd(false)}
        onOk={guardarPassword}
        okText="Guardar contraseña"
        confirmLoading={guardandoPwd}
      >
        <Alert
          type="warning"
          message="La contraseña anterior quedará inválida inmediatamente."
          showIcon style={{ marginBottom: 12 }} />
        <Form form={formPwd} layout="vertical">
          <Form.Item
            label="Nueva contraseña"
            name="nuevaPassword"
            rules={[
              { required: true, message: "Ingrese la nueva contraseña." },
              { min: 6, message: "Mínimo 6 caracteres." },
            ]}>
            <Input.Password placeholder="Mínimo 6 caracteres" />
          </Form.Item>
          <Form.Item
            label="Confirmar contraseña"
            name="confirmar"
            dependencies={["nuevaPassword"]}
            rules={[
              { required: true, message: "Confirme la contraseña." },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("nuevaPassword") === value)
                    return Promise.resolve();
                  return Promise.reject(new Error("Las contraseñas no coinciden."));
                },
              }),
            ]}>
            <Input.Password placeholder="Repita la contraseña" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
